import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Percent, Lock } from "lucide-react";

interface Trade {
  trade_code: string;
  name: string;
}

interface TradeCommission {
  trade_code: string;
  rate_percent: number;
  max_percent: number;
}

const DEFAULT_MAX = 15;

export function PerTradeCommissionPanel({ orgId }: { orgId: string }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState(false);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [rows, setRows] = useState<Record<string, TradeCommission>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!orgId || !user) return;
    (async () => {
      setLoading(true);

      const [{ data: roleData }, { data: activeTrades }, { data: existing }] = await Promise.all([
        supabase.rpc("is_org_admin", { _user_id: user.id, _org_id: orgId }),
        supabase.rpc("get_org_active_trades", { _org_id: orgId }),
        supabase.from("org_trade_commissions").select("trade_code, rate_percent, max_percent").eq("org_id", orgId),
      ]);

      setIsAdmin(Boolean(roleData));

      const tradeList: Trade[] = (activeTrades || []).map((t: any) => ({
        trade_code: t.trade_code,
        name: t.name,
      }));
      setTrades(tradeList);

      const map: Record<string, TradeCommission> = {};
      tradeList.forEach((t) => {
        map[t.trade_code] = { trade_code: t.trade_code, rate_percent: 0, max_percent: DEFAULT_MAX };
      });
      (existing || []).forEach((r: any) => {
        map[r.trade_code] = {
          trade_code: r.trade_code,
          rate_percent: Number(r.rate_percent),
          max_percent: Number(r.max_percent),
        };
      });
      setRows(map);
      setLoading(false);
    })();
  }, [orgId, user]);

  const update = (code: string, patch: Partial<TradeCommission>) => {
    setRows((prev) => {
      const cur = prev[code] || { trade_code: code, rate_percent: 0, max_percent: DEFAULT_MAX };
      const next = { ...cur, ...patch };
      // enforce cap on rate
      if (next.rate_percent > next.max_percent) next.rate_percent = next.max_percent;
      return { ...prev, [code]: next };
    });
  };

  const save = async () => {
    setSaving(true);
    const payload = Object.values(rows).map((r) => ({
      org_id: orgId,
      trade_code: r.trade_code,
      rate_percent: r.rate_percent,
      max_percent: r.max_percent,
    }));
    const { error } = await supabase
      .from("org_trade_commissions")
      .upsert(payload, { onConflict: "org_id,trade_code" });
    setSaving(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Commissions saved" });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">Loading…</CardContent>
      </Card>
    );
  }

  if (trades.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Percent className="w-5 h-5" />
            Per-Trade Commissions
          </CardTitle>
          <CardDescription>
            Enable trades in Settings → Trades to configure per-trade commissions.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Percent className="w-5 h-5" />
          Per-Trade Commissions
        </CardTitle>
        <CardDescription>
          Set the default commission % per trade. Admins control the maximum allowed cap.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {trades.map((t) => {
          const row = rows[t.trade_code];
          if (!row) return null;
          return (
            <div key={t.trade_code} className="space-y-3 border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="font-medium">{t.name}</div>
                <Badge variant="secondary">{row.rate_percent.toFixed(1)}%</Badge>
              </div>

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">
                  Commission rate (capped at {row.max_percent}%)
                </Label>
                <Slider
                  value={[row.rate_percent]}
                  min={0}
                  max={row.max_percent}
                  step={0.25}
                  onValueChange={([v]) => update(t.trade_code, { rate_percent: v })}
                />
              </div>

              <div className="flex items-center gap-2">
                <Lock className="w-3 h-3 text-muted-foreground" />
                <Label htmlFor={`cap-${t.trade_code}`} className="text-xs text-muted-foreground">
                  Admin cap (%)
                </Label>
                <Input
                  id={`cap-${t.trade_code}`}
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={row.max_percent}
                  disabled={!isAdmin}
                  onChange={(e) => {
                    const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                    update(t.trade_code, { max_percent: v });
                  }}
                  className="w-24 h-8"
                />
                {!isAdmin && (
                  <span className="text-xs text-muted-foreground">Locked — admins only</span>
                )}
              </div>
            </div>
          );
        })}

        <Button onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Commissions"}
        </Button>
      </CardContent>
    </Card>
  );
}
